import React, { useMemo, useState, useEffect, useCallback } from 'react';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import { FieldProps, FastFieldProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { withField } from './withFields';
import withTooltip from 'components/Tooltip/withInfo';
import { getErrorMessageKey } from 'helpers/helpers';
import { useValueStyles } from './styles';

interface OwnProps {
  name: string;
  specificError?: boolean;
  endAdornment?: string | React.ReactNode;
  readOnly?: boolean;
  maxLength?: number;
  afterChange?: () => void;
}

type Props = OwnProps & TextFieldProps & (FieldProps | FastFieldProps);

/**
 * Basic wrapper around Mui TextField. Main purpose of this component is to set certain repeating
 * things like variant and to be again to be wrapped in formik's field which inject more props
 * like form and field to it that handle changes, submits and errors
 *
 * @param name {string} - id and name for the input and name of formik value
 * @param specificError {boolean} - optional parameter that tell the component to display specific error from backend instead of generic one
 * @param endAdornment {string} - thing that will be suffixed to the field (currency,...)
 * @param TextFieldProps {any} - any material-ui TextField props can be applied
 * @param readOnly {boolean} - shortcut to get readOnly so it's not needed to weap it in the InputProps
 */
const CustomFormTextField: React.FunctionComponent<Props> = React.memo(
  ({
    field, // from formik
    meta, // from formik
    specificError,
    InputProps,
    endAdornment,
    readOnly,
    maxLength,
    afterChange,
    ...rest
  }) => {
    const { t } = useTranslation();
    const classes = useValueStyles();
    const [callAfterChange, setCallAfterChange] = useState(false);

    const enhancedInputProps = useMemo(
      () => ({
        readOnly,
        endAdornment: endAdornment && (
          <InputAdornment position="end">{endAdornment}</InputAdornment>
        ),
        classes: {
          root: readOnly ? classes.readOnly : '',
        },
        ...InputProps,
      }),
      [InputProps, classes.readOnly, endAdornment, readOnly],
    );

    const errorMessage = useMemo(
      () => (meta.error ? t(getErrorMessageKey(meta.error, specificError)) : ''),
      [meta.error, specificError, t],
    );

    const inputProps = useMemo(
      () => ({
        maxLength: maxLength || 50,
      }),
      [maxLength],
    );

    useEffect(() => {
      if (callAfterChange) {
        afterChange?.();
        setCallAfterChange(false);
      }
    }, [callAfterChange, afterChange]);

    const onChange = useCallback(
      (e: React.FocusEvent<any>) => {
        if (afterChange) {
          setCallAfterChange(true);
        }
        field.onChange(e);
      },
      [afterChange, field],
    );

    return (
      <TextField
        size="small"
        InputProps={enhancedInputProps}
        inputProps={inputProps}
        {...field}
        error={rest.error || !!errorMessage}
        helperText={errorMessage}
        {...rest}
        onChange={onChange}
      />
    );
  },
);

const FormTextField = withField(withTooltip(CustomFormTextField));

export default FormTextField;
